import React, {
  createContext,
  useContext,
  ReactNode,
  ReactElement,
} from 'react';
import whatsappAvatarCredex from '../../assets/images/icons/avatarcredex.webp';
import whatsappAvatarZu from '../../assets/images/icons/avatarzu.webp';
import approvalIcon from '../../assets/images/icons/approval.svg';
import guarantorIcon from '../../assets/images/icons/guarantor.svg';
import flexibleIcon from '../../assets/images/icons/flexible.svg';
import commerceIcon from '../../assets/images/icons/commerce.svg';
import {
  faSearch,
  faLightbulb,
  faHandPointRight,
  faCheckSquare,
  faShieldAlt,
  faMobileAlt,
  faUserCheck,
  faCreditCard,
  faDownload,
  faChartLine,
  faLock,
  faClock,
  faStar,
  faCheckCircle,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

export interface HowItWorksStep {
  title: string;
  text: string;
}

export interface HowItWorksConfig {
  sectionTitle: string;
  steps: HowItWorksStep[];
  cta: {
    title: string;
    text: string;
    buttonText: string;
  };
}

export interface CtaBannerConfig {
  badge: string;
  title: string;
  description: string;
  buttonText: string;
}

export interface AboutUsStat {
  value: string;
  label: string;
}

export interface AboutUsBenefit {
  icon: string;
  title: string;
  description: string;
}

export interface AboutUsValue {
  title: string;
  content: string;
  icon: IconDefinition | any;
}

export interface AboutUsVideoConfig {
  url: string;
  poster?: string;
  title?: string;
  badge?: string;
}

export interface DownloadAppStat {
  value: string;
  label: string;
  icon?: IconDefinition | any;
}

export interface DownloadAppFeatureItem {
  id: string;
  tag: string;
  title: string;
  description: string;
  highlights: string[];
  imageKey: 'downloadApp' | 'downloadAppFeatures';
}

export interface DownloadAppStep {
  number: string;
  title: string;
  description: string;
  icon: IconDefinition | any;
}

export interface DownloadAppSecurityBadge {
  title: string;
  description: string;
  icon: IconDefinition | any;
}

export interface DownloadAppConfig {
  hero: {
    badge: string;
    title: string;
    subtitle: string;
  };
  stats: DownloadAppStat[];
  featureItems: DownloadAppFeatureItem[];
  steps: {
    title: string;
    subtitle: string;
    items: DownloadAppStep[];
  };
  security: {
    title: string;
    subtitle: string;
    badges: DownloadAppSecurityBadge[];
  };
  quickFeatures: {
    title: string;
    subtitle: string;
  };
  cta: {
    title: string;
    subtitle: string;
  };
}

export interface AboutUsConfig {
  whoWeAre: {
    title: string;
    text: string;
    stats: AboutUsStat[];
  };
  accessibleSolutions: {
    title: string;
    text: string;
  };
  growth: {
    title: string;
    paragraphs: string[];
  };
  video?: AboutUsVideoConfig;
  summaryBox: {
    quote: string;
    highlight: string;
    legalNote: string;
  };
  benefitsSection: {
    title: string;
    benefits: AboutUsBenefit[];
    cta: {
      title: string;
      description: string;
      whatsappMessage: string;
    };
  };
  mission: {
    title: string;
    text: string;
    highlights: string[];
  };
  vision: {
    title: string;
    text: string;
    highlights: string[];
  };
  valuesSection: {
    title: string;
    values: AboutUsValue[];
  };
}

export interface BrandFeatures {
  hasPaymentPointsDropdown: boolean;
  hasMobileAppSection: boolean;
  hasContactUsImages: boolean;
  hasSalesSection: boolean;
  hasAffiliatedCommerceMenu: boolean;
  hasSpecialContainerOnHome: boolean;
  hasFinancialEducationSection: boolean;
  hasLoyaltyProgram: boolean;
  hasClaimProcessSection: boolean;
  hasCreditSimulator: boolean;
  hasExchangeRate: boolean;
  hasFactoring: boolean;
  hasInvestments: boolean;
  hasInstantCashBanner: boolean;
  hasCompanyProductTab: boolean;
  /** Si es true, el botón de solicitud de crédito abre una URL externa en lugar del modal interno */
  hasExternalCreditProduct: boolean;
  hasPromotionsSection: boolean;
  hasAboutUsVideo: boolean;
}

export interface BrandConfig {
  id: 'credex' | 'zu';
  country: 'NI' | 'GT';
  countryName: string;
  name: string;
  currency: string;
  localCurrencyId: number;
  images: {
    finiquitoBanner: string;
    customerService: string;
    aboutUs: string;
    mission: string;
    vision: string;
    defaultAffiliatedLogo: string;
    paymentPoint: string;
    ctaBanner?: string;
    aboutUsWhoWeAre?: string;
    aboutUsAccessible?: string;
    aboutUsGrowth?: string;
    training?: string;
    products?: string;
    faqs?: string;
    faqsIllustration?: string;
    downloadApp?: string;
    downloadAppFeatures?: string;
    downloadAppBg?: string;
    downloadAppBgMobile?: string;
    downloadAppPhone?: string;
    downloadAppFeaturesBg?: string;
    sales?: string;
    affiliated?: string;
    factoring?: string;
    mobileHeroImages?: {
      aboutUs?: string;
      customerService?: string;
      affiliated?: string;
      instantCash?: string;
      paymentPoint?: string;
      training?: string;
      products?: string;
      faqs?: string;
      factoring?: string;
    };
  };
  assets: {
    mainCarousel: string;
  };
  logos: {
    main: string;
    class: string;
  };
  contact: {
    whatsappNumber: string;
    whatsappAvatar: string;
    email: string;
    phone: string;
  };
  salesContact?: {
    name: string;
    email: string;
    phone: string;
    whatsapp: string;
  };
  social: {
    facebookUrl: string;
  };
  urls: {
    website: string;
    /** URL externa para solicitar crédito (solo aplica cuando hasExternalCreditProduct = true) */
    externalCreditUrl: string;
    iosUrl: string;
    androidUrl: string;
  };
  validation: {
    phonePattern: string;
    documentIdPattern: string;
  };
  descriptions: {
    aboutUs: string;
    affiliated: string;
    paymentPoint?: string;
    customerService?: string;
    faqs?: string;
    faqsTitle?: string;
  };
  maps: {
    pathMap: string;
    defaultDepartment: string;
  };
  howItWorks: HowItWorksConfig;
  ctaBanner: CtaBannerConfig;
  aboutUs: AboutUsConfig;
  downloadAppConfig?: DownloadAppConfig;
  recaptchaSiteKey: string;
  features: BrandFeatures;
}

import credexMainLogo from '../../assets/images/logos/main-logo.svg';
import zuMainLogo from '../../assets/images/logos/zu-main-logo.svg';
import defaultCaCredex from '../../assets/images/icons/defult-ca.webp';
import defaultCaZu from '../../assets/images/icons/defult-ca-zu.webp';
import bgQuienesSomosZu from '../../assets/images/tenants/zu/about-us/background-quienes-somos-desktop.webp';
import missionImgCredex from '../../assets/images/tenants/credex/about-us/mission-imagen.webp';
import vissionImgCredex from '../../assets/images/tenants/credex/about-us/vision-image.webp';
import missionImgZu from '../../assets/images/tenants/zu/about-us/mission-imagen.webp';
import vissionImgZu from '../../assets/images/tenants/zu/about-us/vision-image.webp';
import bgFiniquitoZu from '../../assets/images/tenants/zu/pages/baclkground-finiquito-desktop.webp';
import bgPaymentPointZu from '../../assets/images/tenants/zu/pages/background-puntos-pago.webp';
import accessibleZu from '../../assets/images/tenants/zu/affiliated/background-comercios-afiliados.webp';
import growthZu from '../../assets/images/tenants/zu/about-us/vision-image.webp';
import growthCredex from '../../assets/images/tenants/credex/about-us/vision-image.webp';
import quienesSomosCredex from '../../assets/images/tenants/credex/about-us/quienes-somos.webp';
import mobileQuienesSomosZu from '../../assets/images/tenants/zu/about-us/background-quienes-somos-mobile.webp';
import mobileCustomerServiceZu from '../../assets/images/tenants/zu/pages/servicio-al-cliente.webp';
import mobileAffiliatedZu from '../../assets/images/tenants/zu/affiliated/comercio-afiliado.webp';
import mobileInstantCashZu from '../../assets/images/tenants/zu/pages/efectivo-al-instante.webp';
import mobilePaymentPointZu from '../../assets/images/tenants/zu/pages/puntos-de-pago.webp';
import bgCapacitacionZu from '../../assets/images/tenants/zu/pages/background-centro-capacitacion-desktop.webp';
import mobileCapacitacionZu from '../../assets/images/tenants/zu/pages/background-centro-capacitacion-mobile.webp';
import bgProductsZu from '../../assets/images/tenants/zu/pages/background-nuestros-productos-desktop.webp';
import mobileProductsZu from '../../assets/images/tenants/zu/pages/background-nuestros-productos-mobile.webp';
import appMobileImgCredex from '../../assets/images/tenants/credex/download-app/subhero-dowload-app.webp';
import subheroDownloadAppCredex from '../../assets/images/tenants/credex/download-app/subhero-dowload-app.webp';
import subheroDownloadAppMovilCredex from '../../assets/images/tenants/credex/download-app/subhero-download-app-movil.webp';
import zuAppIphoneCredex from '../../assets/images/tenants/credex/download-app/zuApp-Iphone.webp';
import backgroundSectionCredex from '../../assets/images/tenants/credex/download-app/background-section.webp';
import appMobile2ImgCredex from '../../assets/images/tenants/credex/download-app/zuApp-Iphone.webp';
import salesBannerCredex from '../../assets/images/tenants/credex/pages/sales.webp';
import heroAtencionClienteDesktop from '../../assets/images/tenants/credex/sub-pages/hero/hero-atencion-cliente-desktop.webp';
import heroAtencionClienteMobile from '../../assets/images/tenants/credex/sub-pages/hero/hero-atencion-cliente-mobile.webp';
import heroComerciosAfiliadosDesktop from '../../assets/images/tenants/credex/sub-pages/hero/hero-comercios-afiliados-desktop.webp';
import heroComerciosAfiliadosMobile from '../../assets/images/tenants/credex/sub-pages/hero/hero-comercios-afiliados-mobile.webp';
import heroFactoringDesktop from '../../assets/images/tenants/credex/sub-pages/hero/hero-factoring-desktop.webp';
import heroFactoringMobile from '../../assets/images/tenants/credex/sub-pages/hero/hero-factoring-mobile.webp';
import heroPuntosDePagoDesktop from '../../assets/images/tenants/credex/sub-pages/hero/hero-puntos-de-pago-desktop.webp';
import heroPuntosDePagoMobile from '../../assets/images/tenants/credex/sub-pages/hero/hero-puntos-de-pago-mobile.webp';
import heroSobreNosotrosDesktop from '../../assets/images/tenants/credex/sub-pages/hero/hero-sobre-nosotros-desktop.webp';
import heroSobreNosotrosMobile from '../../assets/images/tenants/credex/sub-pages/hero/hero-sobre-nosotros-mobile.webp';

const TENANTS_CONFIG: Record<string, BrandConfig> = {
  credex: {
    id: 'credex',
    country: 'NI',
    countryName: 'Nicaragua',
    name: 'Credex',
    currency: 'C$',
    localCurrencyId: 1,
    images: {
      finiquitoBanner: heroAtencionClienteDesktop,
      customerService: heroAtencionClienteDesktop,
      aboutUs: heroSobreNosotrosDesktop,
      mission: missionImgCredex,
      vision: vissionImgCredex,
      defaultAffiliatedLogo: defaultCaCredex,
      paymentPoint: heroPuntosDePagoDesktop,
      ctaBanner: heroAtencionClienteDesktop,
      aboutUsWhoWeAre: quienesSomosCredex,
      aboutUsAccessible: heroAtencionClienteDesktop,
      aboutUsGrowth: growthCredex,
      faqs: heroAtencionClienteDesktop,
      downloadApp: appMobileImgCredex,
      downloadAppFeatures: appMobile2ImgCredex,
      downloadAppBg: subheroDownloadAppCredex,
      downloadAppBgMobile: subheroDownloadAppMovilCredex,
      downloadAppPhone: zuAppIphoneCredex,
      downloadAppFeaturesBg: backgroundSectionCredex,
      sales: salesBannerCredex,
      affiliated: heroComerciosAfiliadosDesktop,
      factoring: heroFactoringDesktop,
      products: heroSobreNosotrosDesktop,
      mobileHeroImages: {
        customerService: heroAtencionClienteMobile,
        paymentPoint: heroPuntosDePagoMobile,
        faqs: heroAtencionClienteMobile,
        aboutUs: heroSobreNosotrosMobile,
        affiliated: heroComerciosAfiliadosMobile,
        factoring: heroFactoringMobile,
        products: heroSobreNosotrosMobile,
      },
    },
    descriptions: {
      aboutUs:
        'Ofrecemos soluciones financieras innovadoras, sencillas y responsables que promueven el crecimiento de nuestros clientes.',
      affiliated:
        'Explora nuestra amplia red de comercios asociados y realiza tus compras en cuotas fácilmente.',
      paymentPoint:
        'Ubica nuestras sucursales y canales autorizados para realizar tus abonos y transferencias de manera ágil y segura.',
      customerService:
        'Estamos comprometidos con tu tranquilidad. Contáctanos por cualquiera de nuestros medios para asistirte de inmediato.',
      faqs:
        'En esta sección puedes encontrar las preguntas frecuentes (FAQ) y sus respuestas basadas en la experiencia de nuestros clientes. Si tienes alguna duda o pregunta que no haya sido incluida aquí, recuerda que puedes usar nuestra sección de contacto.',
    },
    assets: {
      mainCarousel: 'main-carousel',
    },
    logos: {
      main: credexMainLogo,
      class: process.env.REACT_APP_MAIN_LOGO_CLASS || 'credex-logo',
    },
    contact: {
      whatsappNumber: process.env.REACT_APP_WHATSAPP || '',
      whatsappAvatar: whatsappAvatarCredex,
      email:
        process.env.REACT_APP_CUSTOMER_EMAIL_COMUNICATION ||
        'atencion.clientes@credex.com.ni',
      phone: process.env.REACT_APP_CUSTOMER_SERVICE_PHONE || '',
    },
    salesContact: {
      name: 'Marisela Olmedo',
      phone: '50578258909,1505',
      whatsapp: '50578258909',
      email: 'administracion@credex.com.ni',
    },
    social: {
      facebookUrl: 'https://www.facebook.com/CredexNicaragua',
    },
    urls: {
      website: process.env.REACT_APP_WEB_URL || '',
      externalCreditUrl: 'https://solicitud.credex.com.ni',
      iosUrl: 'https://apps.apple.com/ni/app/z%C3%BC/id6444383730',
      androidUrl: 'https://play.google.com/store/apps/details?id=com.fid.wallet&pcampaignid=web_share',
    },
    validation: {
      phonePattern: process.env.REACT_APP_PHONE_PATTER || '',
      documentIdPattern: process.env.REACT_APP_DOCUMENT_ID_PATTER || '',
    },
    maps: {
      pathMap: process.env.REACT_APP_PATH_MAP || '',
      defaultDepartment: process.env.REACT_APP_DEFAULT_DEPARTMENT || '',
    },
    howItWorks: {
      sectionTitle: 'Así de fácil funciona',
      steps: [
        {
          title: 'Solicita tu crédito en línea',
          text: 'Completa tu solicitud desde cualquier dispositivo en menos de 5 minutos, sin papeleo.',
        },
        {
          title: 'Proceso seguro y confiable',
          text: 'Tu información está protegida con cifrado de nivel bancario. Sin sorpresas ni letra pequeña.',
        },
        {
          title: 'Atención inmediata',
          text: 'Recibe tu aprobación en minutos y el dinero en tu cuenta el mismo día.',
        },
      ],
      cta: {
        title: '¿Listo para tu crédito?',
        text: 'Sin aval ni historial requerido. Solo tú y tu oportunidad.',
        buttonText: 'SOLICÍTALO AHORA',
      },
    },
    ctaBanner: {
      badge: 'Crédito Rápido',
      title: '¿Listo para solicitar tu crédito?',
      description:
        'Obtén la financiación que necesitas de forma rápida y sencilla. ¡Contáctanos por WhatsApp y descubre todas nuestras opciones!',
      buttonText: 'Solicitar mi crédito',
    },
    aboutUs: {
      whoWeAre: {
        title: 'Quiénes Somos',
        text: 'En Credex creemos que detrás de cada meta existe una historia, un sueño y una razón para seguir adelante. Por eso hemos creado un ecosistema financiero diseñado para acompañar a personas, familias y negocios en cada etapa de su crecimiento, conectándolos con oportunidades que les permitan avanzar, prosperar y construir el futuro que desean.',
        stats: [
          { value: '6+', label: 'Años de experiencia' },
          { value: '200K+', label: 'Clientes atendidos' },
        ],
      },
      accessibleSolutions: {
        title: 'Soluciones financieras accesibles',
        text: 'Con años de experiencia en el mercado y más de 200,000 clientes atendidos, ofrecemos soluciones ágiles, accesibles y confiables que incluyen créditos en efectivo, financiamiento de compras en cuotas a través de nuestra red de comercios afiliados, líneas de crédito revolvente, factoraje y alternativas financieras que impulsan el desarrollo de nuestros clientes.',
      },
      growth: {
        title: 'Acompañamos tu crecimiento',
        paragraphs: [
          'También trabajamos junto a comercios, distribuidores y aliados comerciales, brindando herramientas que facilitan el acceso al financiamiento, fortalecen las ventas y generan nuevas oportunidades de crecimiento.',
          'Cada solución que creamos responde a un mismo propósito: acercar a las personas a aquello que desean alcanzar. Porque entendemos que detrás de cada crédito existe una meta, una familia, un negocio o un sueño que merece convertirse en realidad.',
        ],
      },
      summaryBox: {
        quote: 'En Credex no solo financiamos proyectos. Impulsamos movilidad, crecimiento, bienestar y oportunidades para construir un mejor futuro.',
        highlight: 'Haz realidad tus metas.',
        legalNote: 'Credex es una marca de FID Nicaragua, S. A.',
      },
      benefitsSection: {
        title: 'Beneficios',
        benefits: [
          {
            icon: approvalIcon,
            title: 'Acceso Digital',
            description: 'Solicita desde tu celular sin salir de casa, en cualquier momento.',
          },
          {
            icon: guarantorIcon,
            title: 'Sin Filas',
            description: 'Olvídate de esperar. Tu crédito aprobado en minutos.',
          },
          {
            icon: flexibleIcon,
            title: 'Plazos Flexibles',
            description: 'Elige el plazo que mejor se adapta a tu bolsillo.',
          },
          {
            icon: commerceIcon,
            title: '100% Seguro',
            description: 'Proceso cifrado y certificado. Tus datos siempre protegidos.',
          },
        ],
        cta: {
          title: 'Solicita tu Credex',
          description: 'Empieza ahora, sin complicaciones ni papeleo.',
          whatsappMessage: 'Hola, me interesa solicitar mi Credex',
        },
      },
      mission: {
        title: 'Nuestra Misión',
        text: 'Ofrecer soluciones financieras innovadoras, sencillas y responsables que promuevan el crecimiento económico de nuestros clientes, socios y colaboradores en las comunidades donde operamos.',
        highlights: [
          'Crédito accesible para todos',
          'Proceso 100% digital',
          'Sin barreras ni papeleo',
        ],
      },
      vision: {
        title: 'Nuestra Visión',
        text: 'Ser la opción financiera de preferencia de nuestros clientes con la red de asociados y comercios afiliados más grande de Nicaragua.',
        highlights: [
          'Presencia en todo el país',
          'La red de comercios más grande',
          'Soluciones a tu medida',
        ],
      },
      valuesSection: {
        title: 'Nuestros Valores',
        values: [
          {
            title: 'Responsabilidad',
            content: 'Propiciamos un modelo de negocio comprometido, responsable y ético entre todos nuestros actores estratégicos.',
            icon: faCheckSquare,
          },
          {
            title: 'Innovación',
            content: 'Diseñamos constantemente soluciones sostenibles para satisfacer las necesidades de nuestros clientes.',
            icon: faLightbulb,
          },
          {
            title: 'Excelencia',
            content: 'Garantizamos respuestas efectivas que superan las expectativas de nuestros clientes.',
            icon: faHandPointRight,
          },
          {
            title: 'Transparencia',
            content: 'Generamos relaciones de confianza de largo plazo, basadas en la honestidad y en el compromiso.',
            icon: faSearch,
          },
        ],
      },
    },
    downloadAppConfig: {
      hero: {
        badge: 'Tu aplicación financiera',
        title: 'Tu crédito, siempre en tu bolsillo',
        subtitle:
          'Consulta tus líneas de crédito, revisa tus fechas de pago, monitorea tus saldos en tiempo real y aprovecha promociones exclusivas desde tu teléfono.',
      },
      stats: [
        { value: '+10,000', label: 'Descargas en tiendas', icon: faDownload },
        { value: '4.8 ★', label: 'Calificación de usuarios', icon: faStar },
        { value: '24/7', label: 'Disponibilidad total', icon: faClock },
        { value: '256-bit', label: 'Seguridad bancaria SSL', icon: faLock },
      ],
      featureItems: [
        {
          id: 'balance-control',
          tag: 'Control Total',
          title: 'Monitorea tu línea de crédito en tiempo real',
          description:
            'Visualiza tu saldo disponible, límite autorizado, cuota del mes y saldo a liquidar con total claridad y sin sorpresas.',
          highlights: [
            'Disponible y saldo actualizados al instante',
            'Desglose claro de cuota activa y máxima',
            'Historial de movimientos y transacciones',
          ],
          imageKey: 'downloadApp',
        },
        {
          id: 'payment-schedule',
          tag: 'Cero Complicaciones',
          title: 'Calendario inteligente y control de pagos',
          description:
            'Planifica tus cuotas con anticipación y mantén un historial crediticio impecable con recordatorios oportunos de tus fechas de corte.',
          highlights: [
            'Fechas de corte y vencimiento siempre a la vista',
            'Detalle de operaciones por cada línea de crédito',
            'Consulta de cuotas pendientes sin filas',
          ],
          imageKey: 'downloadAppFeatures',
        },
        {
          id: 'promotions-commerce',
          tag: 'Beneficios Exclusivos',
          title: 'Promociones y red de comercios afiliados',
          description:
            'Descubre cientos de comercios en todo el país donde puedes comprar en cuotas y acceder a descuentos y beneficios exclusivos para clientes.',
          highlights: [
            'Amplia red de comercios asociados a nivel nacional',
            'Promociones exclusivas en compras en cuotas',
            'Simulador rápido para calcular tus compras',
          ],
          imageKey: 'downloadApp',
        },
      ],
      steps: {
        title: 'Así de fácil es comenzar',
        subtitle:
          'Descarga la app, ingresa tus datos y toma el control de tus finanzas en menos de 2 minutos.',
        items: [
          {
            number: '01',
            title: 'Descarga la App',
            description:
              'Disponible gratis en Google Play para Android y App Store para iOS.',
            icon: faMobileAlt,
          },
          {
            number: '02',
            title: 'Inicia Sesión Seguro',
            description:
              'Ingresa con tu número de identificación y tu clave personal autorizada.',
            icon: faUserCheck,
          },
          {
            number: '03',
            title: '¡Toma el Control!',
            description:
              'Gestiona tus líneas de crédito, revisa movimientos y compra en cuotas.',
            icon: faChartLine,
          },
        ],
      },
      security: {
        title: 'Seguridad y privacidad de nivel institucional',
        subtitle:
          'Tus datos y transacciones están protegidos por los protocolos más rigurosos de la industria financiera.',
        badges: [
          {
            title: 'Cifrado Bancario SSL',
            description:
              'Toda la información viaja protegida con cifrado de 256 bits de extremo a extremo.',
            icon: faShieldAlt,
          },
          {
            title: 'Autenticación Biométrica',
            description:
              'Acceso rápido y seguro mediante huella dactilar o reconocimiento facial.',
            icon: faLock,
          },
          {
            title: 'Monitoreo 24/7',
            description:
              'Sistemas automatizados de detección de anomalías y prevención de fraude.',
            icon: faCheckCircle,
          },
          {
            title: 'Transparencia Total',
            description:
              'Información clara sin cargos ocultos ni letras pequeñas en tus operaciones.',
            icon: faCreditCard,
          },
        ],
      },
      quickFeatures: {
        title: 'Todo lo que puedes hacer con Credex Móvil',
        subtitle:
          'Una plataforma intuitiva desarrollada para darte rapidez y tranquilidad.',
      },
      cta: {
        title: '¿Listo para llevar el control de tu crédito?',
        subtitle:
          'Descarga Credex Móvil hoy mismo y disfruta de todos los beneficios directamente desde tu celular.',
      },
    },
    recaptchaSiteKey: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '',
    features: {
      hasPaymentPointsDropdown: true,
      hasMobileAppSection: true,
      hasContactUsImages: true,
      hasSalesSection: true,
      hasAffiliatedCommerceMenu: true,
      hasSpecialContainerOnHome: true,
      hasFinancialEducationSection: true,
      hasLoyaltyProgram: true,
      hasClaimProcessSection: true,
      hasCreditSimulator: true,
      hasExchangeRate: true,
      hasFactoring: true,
      hasInvestments: true,
      hasInstantCashBanner: false,
      hasCompanyProductTab: true,
      hasExternalCreditProduct: true,
      hasPromotionsSection: true,
      hasAboutUsVideo: false,
    },
  },
  zu: {
    id: 'zu',
    country: 'GT',
    countryName: 'Guatemala',
    name: 'Zü',
    currency: 'Q',
    localCurrencyId: 2,
    images: {
      finiquitoBanner: bgFiniquitoZu,
      customerService: bgFiniquitoZu,
      aboutUs: bgQuienesSomosZu,
      mission: missionImgZu,
      vision: vissionImgZu,
      defaultAffiliatedLogo: defaultCaZu,
      paymentPoint: bgPaymentPointZu,
      ctaBanner: accessibleZu,
      aboutUsWhoWeAre: bgQuienesSomosZu,
      aboutUsAccessible: accessibleZu,
      aboutUsGrowth: growthZu,
      training: bgCapacitacionZu,
      products: bgProductsZu,
      faqs: bgFiniquitoZu,
      downloadApp: appMobileImgCredex,
      downloadAppFeatures: appMobile2ImgCredex,
      sales: '',
      affiliated: accessibleZu,
      mobileHeroImages: {
        aboutUs: mobileQuienesSomosZu,
        customerService: mobileCustomerServiceZu,
        affiliated: mobileAffiliatedZu,
        instantCash: mobileInstantCashZu,
        paymentPoint: mobilePaymentPointZu,
        training: mobileCapacitacionZu,
        products: mobileProductsZu,
        faqs: mobileCustomerServiceZu,
      },
    },
    descriptions: {
      aboutUs:
        'Transformando el acceso al crédito en Guatemala con rapidez, transparencia y confianza.',
      affiliated:
        'Encuentra los comercios más cercanos y sácale el máximo provecho a tu línea de crédito.',
      paymentPoint:
        'Ubica nuestras sucursales y canales autorizados para realizar tus abonos y transferencias de manera ágil y segura.',
      customerService:
        'Estamos comprometidos con tu tranquilidad. Contáctanos por cualquiera de nuestros medios para asistirte de inmediato.',
      faqs:
        'En esta sección puedes encontrar las preguntas frecuentes (FAQ) y sus respuestas basadas en la experiencia de nuestros clientes. Si tienes alguna duda o pregunta que no haya sido incluida aquí, recuerda que puedes usar nuestra sección de contacto.',
    },
    assets: {
      mainCarousel: 'gt-main-carousel',
    },
    logos: {
      main: zuMainLogo,
      class: process.env.REACT_APP_MAIN_LOGO_CLASS || 'zu-logo',
    },
    contact: {
      whatsappNumber: process.env.REACT_APP_WHATSAPP || '',
      whatsappAvatar: whatsappAvatarZu,
      email: process.env.REACT_APP_CUSTOMER_EMAIL_COMUNICATION || '',
      phone: process.env.REACT_APP_CUSTOMER_SERVICE_PHONE || '',
    },
    salesContact: {
      name: 'Servicio al Cliente',
      phone: '',
      whatsapp: '',
      email: '',
    },
    social: {
      facebookUrl: 'https://www.facebook.com/zucredito',
    },
    urls: {
      website: process.env.REACT_APP_WEB_URL || '',
      externalCreditUrl: '',
      iosUrl: process.env.REACT_APP_IOS_MOBILE_STORE || '',
      androidUrl: process.env.REACT_APP_ANDROID_MOBILE_STORE || '',
    },
    validation: {
      phonePattern: process.env.REACT_APP_PHONE_PATTER || '',
      documentIdPattern: process.env.REACT_APP_DOCUMENT_ID_PATTER || '',
    },
    maps: {
      pathMap: process.env.REACT_APP_PATH_MAP || '',
      defaultDepartment: process.env.REACT_APP_DEFAULT_DEPARTMENT || '',
    },
    howItWorks: {
      sectionTitle: 'Así de fácil funciona',
      steps: [
        {
          title: 'Solicita tu crédito en línea',
          text: 'Completa tu solicitud desde cualquier dispositivo en menos de 5 minutos, sin papeleo.',
        },
        {
          title: 'Proceso seguro y confiable',
          text: 'Tu información está protegida con cifrado de nivel bancario. Sin sorpresas ni letra pequeña.',
        },
        {
          title: 'Atención inmediata',
          text: 'Recibe tu aprobación en minutos y el dinero en tu cuenta el mismo día.',
        },
      ],
      cta: {
        title: '¿Listo para tu crédito?',
        text: 'Sin aval ni historial requerido. Solo tú y tu oportunidad.',
        buttonText: 'SOLICÍTALO AHORA',
      },
    },
    ctaBanner: {
      badge: 'Crédito Rápido',
      title: '¿Listo para solicitar tu crédito?',
      description:
        'Obtén la financiación que necesitas de forma rápida y sencilla. ¡Contáctanos por WhatsApp y descubre todas nuestras opciones!',
      buttonText: 'Solicitar mi crédito',
    },
    aboutUs: {
      whoWeAre: {
        title: 'Quiénes Somos',
        text: 'En Zü Crédito creemos que detrás de cada meta existe una historia, un sueño y una razón para seguir adelante. Por eso hemos creado un ecosistema financiero diseñado para acompañar a personas, familias y negocios en cada etapa de su crecimiento, conectándolos con oportunidades que les permitan avanzar, prosperar y construir el futuro que desean.',
        stats: [
          { value: '6+', label: 'Años de experiencia' },
          { value: '200K+', label: 'Clientes atendidos' },
        ],
      },
      accessibleSolutions: {
        title: 'Soluciones financieras accesibles',
        text: 'Con 6 años de experiencia en el mercado y más de 200,000 clientes atendidos, ofrecemos soluciones ágiles, accesibles y confiables que incluyen créditos en efectivo, financiamiento para vehículos, motocicletas y Tuk Tuks, consolidación de deudas, crédito revolvente para consumo a través de nuestra red de comercios afiliados y alternativas financieras que impulsan el desarrollo de nuestros clientes.',
      },
      growth: {
        title: 'Acompañamos tu crecimiento',
        paragraphs: [
          'También trabajamos junto a comercios, distribuidores y aliados automotrices, brindando herramientas que facilitan el acceso al financiamiento, fortalecen las ventas y generan nuevas oportunidades de crecimiento.',
          'Cada solución que creamos responde a un mismo propósito: acercar a las personas a aquello que desean alcanzar. Porque entendemos que detrás de cada crédito existe una meta, una familia, un negocio o un sueño que merece convertirse en realidad.',
        ],
      },
      video: {
        url: `${process.env.PUBLIC_URL || ''}/assets/videos/zu/video-aboutus.mp4`,
        poster: `${process.env.PUBLIC_URL || ''}/assets/videos/zu/video-aboutus-poster.jpg`,
        title: 'Conoce nuestra historia y propósito',
        badge: 'Video institucional',
      },
      summaryBox: {
        quote: 'En Zü no solo financiamos proyectos. Impulsamos movilidad, crecimiento, bienestar y oportunidades para construir un mejor futuro.',
        highlight: 'Haz realidad tus metas.',
        legalNote: 'Zü es una marca de FID Guatemala, S. A.',
      },
      benefitsSection: {
        title: 'Beneficios',
        benefits: [
          {
            icon: approvalIcon,
            title: 'Acceso Digital',
            description: 'Solicita desde tu celular sin salir de casa, en cualquier momento.',
          },
          {
            icon: guarantorIcon,
            title: 'Sin Filas',
            description: 'Olvídate de esperar. Tu crédito aprobado en minutos.',
          },
          {
            icon: flexibleIcon,
            title: 'Plazos Flexibles',
            description: 'Elige el plazo que mejor se adapta a tu bolsillo.',
          },
          {
            icon: commerceIcon,
            title: '100% Seguro',
            description: 'Proceso cifrado y certificado. Tus datos siempre protegidos.',
          },
        ],
        cta: {
          title: 'Solicita tu Zü',
          description: 'Empieza ahora, sin complicaciones ni papeleo.',
          whatsappMessage: 'Hola, me interesa solicitar mi Zü',
        },
      },
      mission: {
        title: 'Nuestra Misión',
        text: 'Ofrecer soluciones financieras innovadoras, sencillas y responsables que promuevan el crecimiento económico de nuestros clientes, socios y colaboradores en las comunidades donde operamos.',
        highlights: [
          'Crédito accesible para todos',
          'Proceso 100% digital',
          'Sin barreras ni papeleo',
        ],
      },
      vision: {
        title: 'Nuestra Visión',
        text: 'Ser la opción financiera de preferencia de nuestros clientes con la red de asociados y comercios afiliados más grande de Guatemala.',
        highlights: [
          'Presencia en todo el país',
          'La red de comercios más grande',
          'Soluciones a tu medida',
        ],
      },
      valuesSection: {
        title: 'Nuestros Valores',
        values: [
          {
            title: 'Responsabilidad',
            content: 'Propiciamos un modelo de negocio comprometido, responsable y ético entre todos nuestros actores estratégicos.',
            icon: faCheckSquare,
          },
          {
            title: 'Innovación',
            content: 'Diseñamos constantemente soluciones sostenibles para satisfacer las necesidades de nuestros clientes.',
            icon: faLightbulb,
          },
          {
            title: 'Excelencia',
            content: 'Garantizamos respuestas efectivas que superan las expectativas de nuestros clientes.',
            icon: faHandPointRight,
          },
          {
            title: 'Transparencia',
            content: 'Generamos relaciones de confianza de largo plazo, basadas en la honestidad y en el compromiso.',
            icon: faSearch,
          },
        ],
      },
    },
    downloadAppConfig: {
      hero: {
        badge: 'Tu aplicación financiera',
        title: 'Tu crédito, siempre en tu bolsillo',
        subtitle:
          'Consulta tu saldo disponible, revisa tus fechas de pago, monitorea tus transacciones y aprovecha beneficios exclusivos desde tu celular.',
      },
      stats: [
        { value: '+10,000', label: 'Descargas en tiendas', icon: faDownload },
        { value: '4.8 ★', label: 'Calificación de usuarios', icon: faStar },
        { value: '24/7', label: 'Disponibilidad total', icon: faClock },
        { value: '256-bit', label: 'Seguridad bancaria SSL', icon: faLock },
      ],
      featureItems: [
        {
          id: 'balance-control',
          tag: 'Control Total',
          title: 'Monitorea tu crédito en tiempo real',
          description:
            'Visualiza tu disponibilidad, límite aprobado, cuota del mes y saldo con total claridad y sin complicaciones.',
          highlights: [
            'Disponible y saldo actualizados al instante',
            'Desglose claro de cuotas y fechas',
            'Historial de movimientos y transacciones',
          ],
          imageKey: 'downloadApp',
        },
        {
          id: 'payment-schedule',
          tag: 'Cero Complicaciones',
          title: 'Calendario inteligente de pagos',
          description:
            'Planifica tus cuotas con anticipación y mantén un historial crediticio impecable con recordatorios de pago.',
          highlights: [
            'Fechas de corte y vencimiento siempre a la vista',
            'Detalle de operaciones activas',
            'Consulta de cuotas pendientes sin filas',
          ],
          imageKey: 'downloadAppFeatures',
        },
        {
          id: 'promotions-commerce',
          tag: 'Beneficios Exclusivos',
          title: 'Comercios afiliados y promociones',
          description:
            'Descubre la red de comercios asociados donde puedes comprar en cuotas y acceder a promociones especiales.',
          highlights: [
            'Red de comercios asociados a nivel nacional',
            'Promociones exclusivas en compras en cuotas',
            'Simulador rápido de créditos',
          ],
          imageKey: 'downloadApp',
        },
      ],
      steps: {
        title: 'Así de fácil es comenzar',
        subtitle:
          'Descarga la app, ingresa tus datos y toma el control de tus finanzas en menos de 2 minutos.',
        items: [
          {
            number: '01',
            title: 'Descarga la App',
            description:
              'Disponible gratis en Google Play para Android y App Store para iOS.',
            icon: faMobileAlt,
          },
          {
            number: '02',
            title: 'Inicia Sesión Seguro',
            description:
              'Ingresa con tu número de identificación y tu clave personal autorizada.',
            icon: faUserCheck,
          },
          {
            number: '03',
            title: '¡Toma el Control!',
            description:
              'Gestiona tus créditos, revisa movimientos y compra en cuotas.',
            icon: faChartLine,
          },
        ],
      },
      security: {
        title: 'Seguridad y privacidad de nivel institucional',
        subtitle:
          'Tus datos y transacciones están protegidos por los protocolos más rigurosos de la industria financiera.',
        badges: [
          {
            title: 'Cifrado Bancario SSL',
            description:
              'Toda la información viaja protegida con cifrado de 256 bits de extremo a extremo.',
            icon: faShieldAlt,
          },
          {
            title: 'Autenticación Biométrica',
            description:
              'Acceso rápido y seguro mediante huella dactilar o reconocimiento facial.',
            icon: faLock,
          },
          {
            title: 'Monitoreo 24/7',
            description:
              'Sistemas automatizados de detección de anomalías y prevención de fraude.',
            icon: faCheckCircle,
          },
          {
            title: 'Transparencia Total',
            description:
              'Información clara sin cargos ocultos ni letras pequeñas en tus operaciones.',
            icon: faCreditCard,
          },
        ],
      },
      quickFeatures: {
        title: 'Todo lo que puedes hacer con Zü Móvil',
        subtitle:
          'Una plataforma intuitiva desarrollada para darte rapidez y tranquilidad.',
      },
      cta: {
        title: '¿Listo para llevar el control de tu crédito?',
        subtitle:
          'Descarga Zü Móvil hoy mismo y disfruta de todos los beneficios directamente desde tu celular.',
      },
    },
    recaptchaSiteKey: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '',
    features: {
      hasPaymentPointsDropdown: false,
      hasMobileAppSection: false,
      hasContactUsImages: false,
      hasSalesSection: false,
      hasAffiliatedCommerceMenu: false,
      hasSpecialContainerOnHome: false,
      hasFinancialEducationSection: false,
      hasLoyaltyProgram: false,
      hasClaimProcessSection: false,
      hasCreditSimulator: false,
      hasExchangeRate: false,
      hasFactoring: false,
      hasInvestments: false,
      hasInstantCashBanner: true,
      hasCompanyProductTab: false,
      hasExternalCreditProduct: false,
      hasPromotionsSection: false,
      hasAboutUsVideo: true,
    },
  },
};

const BrandContext = createContext<BrandConfig | undefined>(undefined);

export const BrandProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const rawSiteEnv = process.env.REACT_APP_SITE_TITLE || '';

  // Normalización defensiva para evitar fallos por tildes, mayúsculas o espacios
  const normalizedSiteEnv = rawSiteEnv
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  let activeTenantKey = 'credex';
  if (normalizedSiteEnv.includes('zu')) {
    activeTenantKey = 'zu';
  } else if (normalizedSiteEnv.includes('credex')) {
    activeTenantKey = 'credex';
  }

  const currentBrand =
    TENANTS_CONFIG[activeTenantKey] || TENANTS_CONFIG['credex'];

  return (
    <BrandContext.Provider value={currentBrand}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = (): BrandConfig => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand debe usarse dentro de un BrandProvider');
  }
  return context;
};
